import { FileText, Pencil, RefreshCw, Save, Trash2, X } from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import { Link, useNavigate, useParams } from "react-router-dom";
import remarkGfm from "remark-gfm";

import {
  useDeleteDocument,
  useKnowledgeDocument,
  useSyncKnowledge,
  useUpdateDocument,
} from "../api/useKnowledgeApi";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import { SyncStatusBadge } from "../components/SyncStatusBadge";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { Badge } from "@/components/ui/v2/badge";
import { Button } from "@/components/ui/v2/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/v2/card";
import { Input } from "@/components/ui/v2/input";
import { Label } from "@/components/ui/v2/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/v2/select";
import { Skeleton } from "@/components/ui/v2/skeleton";
import { Textarea } from "@/components/ui/v2/textarea";
import { useAuth } from "@/lib/hooks/useAuth";

const CHUNK_TYPES = [
  { value: "domain-knowledge", label: "Domain Knowledge" },
  { value: "q-and-a", label: "Q&A" },
  { value: "example", label: "Example" },
  { value: "procedure", label: "Procedure" },
];

function KnowledgeDocumentDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { integrationName, fileName } = useParams<{
    integrationName: string;
    fileName: string;
  }>();

  const { tenantId } = useAuth();
  const decodedFileName = fileName ? decodeURIComponent(fileName) : "";
  const { data: doc, isLoading } = useKnowledgeDocument(decodedFileName || null);
  const updateMutation = useUpdateDocument();
  const deleteMutation = useDeleteDocument();
  const syncMutation = useSyncKnowledge(tenantId);

  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editChunkType, setEditChunkType] = useState("");
  const [editContent, setEditContent] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);

  const startEditing = useCallback(() => {
    if (!doc) return;
    setEditTitle(doc.title ?? "");
    setEditChunkType(doc.chunkType ?? "");
    setEditContent(doc.content);
    setEditing(true);
  }, [doc]);

  const cancelEditing = useCallback(() => {
    setEditing(false);
  }, []);

  const canSave = editTitle.trim().length > 0 && editChunkType.length > 0 && editContent.trim().length > 0;

  const saveEditing = useCallback(() => {
    if (!decodedFileName || !canSave) return;
    updateMutation.mutate(
      {
        fileName: decodedFileName,
        content: editContent,
        title: editTitle,
        chunkType: editChunkType,
      },
      { onSuccess: () => setEditing(false) },
    );
  }, [decodedFileName, editContent, editTitle, editChunkType, canSave, updateMutation]);

  const handleDelete = useCallback(() => {
    if (!decodedFileName) return;
    deleteMutation.mutate(decodedFileName, {
      onSuccess: () => {
        syncMutation.mutate(undefined, {
          onSettled: () => navigate(-1),
        });
      },
    });
  }, [decodedFileName, deleteMutation, syncMutation, navigate]);

  const backHref = integrationName
    ? `/integrations/${integrationName}/chat`
    : "/integrations";

  const breadcrumbs = (
    <nav className="flex items-center gap-1.5 text-sm text-muted-foreground">
      <Link to="/integrations" className="hover:text-foreground">{t("common.integrations", { defaultValue: "Integrations" })}</Link>
      <span>&gt;</span>
      <Link to={`/integrations/${integrationName}`} className="hover:text-foreground">Captain</Link>
      <span>&gt;</span>
      <Link to={`/integrations/${integrationName}/chat`} className="hover:text-foreground">{t("integrations.modules.names.chat", { defaultValue: "Chat" })}</Link>
      <span>&gt;</span>
      <span className="max-w-[200px] truncate text-foreground">{doc?.title ?? decodedFileName}</span>
    </nav>
  );

  if (isLoading) {
    return (
      <ModernPageLayout breadcrumbs={breadcrumbs} backbutton={{ label: t("common.back"), href: backHref }}>
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-96 w-full" />
        </div>
      </ModernPageLayout>
    );
  }

  if (!doc) {
    return (
      <ModernPageLayout breadcrumbs={breadcrumbs} backbutton={{ label: t("common.back"), href: backHref }}>
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
          <FileText className="h-12 w-12 mb-4" />
          <p>{t("captain.knowledge.documentNotFound")}</p>
        </div>
      </ModernPageLayout>
    );
  }

  return (
    <ModernPageLayout
      breadcrumbs={breadcrumbs}
      title={doc.title ?? decodedFileName}
      subtitle={decodedFileName}
      backbutton={{ label: t("common.back"), href: backHref }}
    >
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-end gap-2">
          {editing ? (
            <>
              <Button type="button" variant="ghost" size="sm" onClick={cancelEditing}>
                <X className="mr-2 h-4 w-4" />
                {t("captain.knowledge.cancelButton")}
              </Button>
              <Button
                size="sm"
                onClick={saveEditing}
                disabled={!canSave}
                loading={updateMutation.isPending}
              >
                <Save className="mr-2 h-4 w-4" />
                {t("captain.knowledge.saveButton")}
              </Button>
            </>
          ) : (
            <>
              {doc.syncStatus !== "synced" && (
                <Button
                  size="sm"
                  onClick={() => syncMutation.mutate()}
                  disabled={syncMutation.isPending}
                >
                  <RefreshCw
                    className={`mr-2 h-4 w-4 ${syncMutation.isPending ? "animate-spin" : ""}`}
                  />
                  {syncMutation.isPending ? t("captain.knowledge.syncing") : t("captain.knowledge.syncAllDocuments", { defaultValue: "Sync all documents" })}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={startEditing}>
                <Pencil className="mr-2 h-4 w-4" />
                {t("captain.knowledge.editButton")}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 className="mr-2 h-4 w-4 text-destructive" />
                {t("captain.knowledge.deleteConfirm")}
              </Button>
            </>
          )}
        </div>

        <Card className="overflow-hidden">
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <div className="shrink-0 rounded-lg bg-muted p-2.5">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <CardTitle className="text-wrap text-base">{doc.title ?? decodedFileName}</CardTitle>
                  <CardDescription className="mt-0.5 text-wrap">{decodedFileName}</CardDescription>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Badge variant="outline">{doc.chunkType ?? "untyped"}</Badge>
                <SyncStatusBadge status={doc.syncStatus} />
              </div>
            </div>
          </CardHeader>
        </Card>

        {editing ? (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t("captain.knowledge.detailsCard")}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="editTitle">{t("captain.knowledge.titleLabel")}</Label>
                    <Input
                      id="editTitle"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                    />
                    {editTitle.trim().length === 0 && (
                      <p className="text-xs text-destructive">{t("captain.knowledge.titleRequired")}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>{t("captain.knowledge.typeLabel")}</Label>
                    <Select value={editChunkType} onValueChange={setEditChunkType}>
                      <SelectTrigger>
                        <SelectValue placeholder={t("captain.knowledge.selectType")} />
                      </SelectTrigger>
                      <SelectContent>
                        {CHUNK_TYPES.map((ct) => (
                          <SelectItem key={ct.value} value={ct.value}>
                            {ct.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {editChunkType.length === 0 && (
                      <p className="text-xs text-destructive">{t("captain.knowledge.typeRequired")}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t("captain.knowledge.contentCard")}</CardTitle>
                <CardDescription>{t("captain.knowledge.editContentDescription")}</CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="min-h-[500px] font-mono text-sm"
                />
                {editContent.trim().length === 0 && (
                  <p className="mt-2 text-xs text-destructive">{t("captain.knowledge.contentRequired")}</p>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("captain.knowledge.contentCard")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="captain-markdown text-sm">
                <Markdown remarkPlugins={[remarkGfm]}>{doc.content}</Markdown>
              </div>
            </CardContent>
          </Card>
        )}

        {doc.similarDocuments.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("captain.knowledge.similarDocuments")}</CardTitle>
              <CardDescription>
                {t("captain.knowledge.similarDocumentsDescription")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {doc.similarDocuments.map((sim) => (
                  <button
                    key={sim.id}
                    type="button"
                    className="flex items-center gap-3 rounded-lg border p-4 text-left transition-all hover:bg-muted/50 hover:shadow-sm"
                    onClick={() =>
                      navigate(
                        `/integrations/${integrationName}/chat/${encodeURIComponent(sim.id.replace("_", "."))}`
                      )
                    }
                  >
                    <div className="rounded-md bg-muted p-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {sim.title ?? sim.id}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t("captain.knowledge.matchPercent", { score: (sim.score * 100).toFixed(0) })}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <DeleteConfirmDialog
        open={deleteOpen}
        onOpenChange={(open) => !open && !deleteMutation.isPending && !syncMutation.isPending && setDeleteOpen(false)}
        fileName={decodedFileName}
        onConfirm={handleDelete}
        isLoading={deleteMutation.isPending || syncMutation.isPending}
      />
    </ModernPageLayout>
  );
}

export default KnowledgeDocumentDetailPage;
